'use client';

import React from 'react';
import { PasswordInput } from '@/components/ui/password-input';
import { signupStyles } from '../../signup-templates/signup.styles';
import { Box, Stack, Button, Field, Input, Alert } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { signupSchema, TSignup } from './signup.validator';
import { zodResolver } from '@hookform/resolvers/zod';
import { requestSignup } from '@/services/auth/auth.service';
import { setCookies } from '@/app/actions/auth/cookies.action';
import { useRouter } from 'next/navigation';
import BarLoader from '@/components/ui/loaders/BarLoader';

const SignupForm = () => {
   const { push } = useRouter();
   const {
      register,
      reset,
      handleSubmit,
      formState: { errors, isSubmitting },
      setError,
   } = useForm<TSignup>({ defaultValues: {}, resolver: zodResolver(signupSchema), mode: 'onBlur' });

   const onSubmit = handleSubmit(async (value: TSignup) => {
      const [error] = await requestSignup(value);

      if (error) {
         setError('root', { message: error.response?.data.message as string, type: 'manual' });
         return console.log(error);
      }

      await setCookies('registered-email', value.email, new Date(Date.now() + 5 * 60 * 1000));
      push('/auth/verify-email');
      return;
   });

   return (
      <form onSubmit={onSubmit}>
         {isSubmitting && <BarLoader loadingText="Signing up..." />}
         <Stack gap="4" align="flex-start" minW="full">
            {errors.root?.message && (
               <Alert.Root status="error">
                  <Alert.Indicator />
                  <Alert.Title>{errors.root.message}</Alert.Title>
               </Alert.Root>
            )}
            <Box css={signupStyles.double_input}>
               <Field.Root invalid={Boolean(errors.first_name?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Name
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Input
                     placeholder="Name"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#202020"
                     {...register('first_name')}
                  />
                  <Field.ErrorText>{errors.first_name?.message}</Field.ErrorText>
               </Field.Root>

               <Field.Root invalid={Boolean(errors.last_name?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Surname
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Input
                     placeholder="Surname"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#202020"
                     {...register('last_name')}
                  />
                  <Field.ErrorText>{errors.last_name?.message}</Field.ErrorText>
               </Field.Root>
            </Box>
            <Field.Root invalid={Boolean(errors.email?.message)}>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Email
                  <Box as="span" color="error">
                     *
                  </Box>
               </Field.Label>
               <Input
                  type="email"
                  placeholder="Email"
                  variant="subtle"
                  h="10"
                  rounded="12px"
                  bg="#202020"
                  {...register('email')}
               />
               <Field.ErrorText>{errors.email?.message}</Field.ErrorText>
            </Field.Root>
            <Box css={signupStyles.double_input}>
               <Field.Root invalid={Boolean(errors.password?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Create Password
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Box mb="2" w="full">
                     <PasswordInput
                        placeholder="Create Password"
                        size="md"
                        h="10"
                        rounded="12px"
                        bg="#202020"
                        {...register('password')}
                     />
                     <Field.ErrorText>{errors.password?.message}</Field.ErrorText>
                  </Box>
               </Field.Root>

               <Field.Root invalid={Boolean(errors.confirmPassword?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Confirm Password
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Box mb="2" w="full">
                     <PasswordInput
                        placeholder="Confirm Password"
                        size="md"
                        h="10"
                        rounded="12px"
                        bg="#202020"
                        {...register('confirmPassword')}
                     />
                     <Field.ErrorText>{errors.confirmPassword?.message}</Field.ErrorText>
                  </Box>
               </Field.Root>
            </Box>
            <Box css={signupStyles.signup_btn_con}>
               <Button
                  type="button"
                  onClick={() => reset()}
                  bg="darkerBtn"
                  textStyle="xs"
                  color="white"
                  h="10"
                  maxW="36"
                  minW="36"
                  p="0"
               >
                  Reset
               </Button>
               <Button textStyle="xs" h="10" maxW="36" minW="36" p="0" type="submit">
                  Verify Email
               </Button>
            </Box>
         </Stack>
      </form>
   );
};

export default SignupForm;
